import React, { useState } from 'react';
import { Table, Button, Space, Modal, Tag, Input, Tooltip, Alert } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { METER_STATUS_LABELS, METER_STATUS } from '../../models/types';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import MeterForm from './MeterForm';

const MeterList = () => {
  const { meters, subscribers, updateMeter, addMeter } = useApp();
  const { user } = useAuth();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedMeter, setSelectedMeter] = useState(null);
  const [searchText, setSearchText] = useState('');
  const isViewer = user?.role === 'viewer';

  const handleAddMeter = () => {
    setSelectedMeter(null);
    setIsFormOpen(true);
  };

  const handleEditMeter = (record) => {
    setSelectedMeter(record);
    setIsFormOpen(true);
  };

  const handleDeleteMeter = (id) => {
    Modal.confirm({
      title: 'Supprimer ce compteur',
      content: 'Êtes-vous sûr ?',
      okText: 'Oui',
      cancelText: 'Non',
      okType: 'danger',
      onOk() {
        // Delete meter logic
      }
    });
  };

  const handleFormSubmit = (values) => {
    if (selectedMeter) {
      updateMeter(selectedMeter.id, values, user?.id);
    } else {
      addMeter(values, user?.id);
    }
    setIsFormOpen(false);
    setSelectedMeter(null);
  };

  const filteredData = meters.filter(meter =>
    meter.serialNumber.toLowerCase().includes(searchText.toLowerCase()) ||
    meter.brand.toLowerCase().includes(searchText.toLowerCase()) ||
    meter.model.toLowerCase().includes(searchText.toLowerCase())
  );

  const getSubscriberName = (subscriberId) => {
    const subscriber = subscribers.find(s => s.id === subscriberId);
    return subscriber ? `${subscriber.firstName} ${subscriber.lastName}` : 'N/A';
  };

  const defectiveMeters = meters.filter(m => m.status === METER_STATUS.DEFECTIVE || m.anomalyAlert);

  const columns = [
    {
      title: 'Série',
      dataIndex: 'serialNumber',
      key: 'serialNumber'
    },
    {
      title: 'Marque/Modèle',
      key: 'brand',
      render: (_, record) => `${record.brand} - ${record.model}`
    },
    {
      title: 'Abonné',
      key: 'subscriber',
      render: (_, record) => getSubscriberName(record.subscriberId)
    },
    {
      title: 'Capacité (m³)',
      dataIndex: 'capacity',
      key: 'capacity'
    },
    {
      title: 'Statut',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        let color = 'green';
        if (status === METER_STATUS.DEFECTIVE) color = 'red';
        else if (status === METER_STATUS.MAINTENANCE) color = 'orange';
        else if (status === METER_STATUS.INACTIVE) color = 'gray';
        return <Tag color={color}>{METER_STATUS_LABELS[status]}</Tag>;
      }
    },
    {
      title: 'Dernière lecture',
      dataIndex: 'lastReading',
      key: 'lastReading'
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_, record) => !isViewer && (
        <Space size="small">
          <Tooltip title="Modifier">
            <Button
              type="default"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEditMeter(record)}
            />
          </Tooltip>
          <Tooltip title="Supprimer">
            <Button
              type="primary"
              danger
              size="small"
              icon={<DeleteOutlined />}
              onClick={() => handleDeleteMeter(record.id)}
            />
          </Tooltip>
        </Space>
      )
    }
  ];

  return (
    <div>
      {defectiveMeters.length > 0 && (
        <Alert
          message={`⚠️ ${defectiveMeters.length} compteur(s) défectueux ou suspects`}
          type="warning"
          showIcon
          style={{ marginBottom: 20 }}
        />
      )}

      {isViewer && (
        <Alert
          message="Mode Consultation"
          description="Vous êtes en mode lecture seule. Les modifications ne sont pas autorisées pour votre rôle."
          type="info"
          showIcon
          style={{ marginBottom: 20 }}
        />
      )}

      <div style={{ marginBottom: 20, display: 'flex', gap: 10 }}>
        <Input
          placeholder="Rechercher (série, marque, modèle)..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{ flex: 1 }}
        />
        {!isViewer && (
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAddMeter}>
            Ajouter un compteur
          </Button>
        )}
      </div>

      <Table
        columns={columns}
        dataSource={filteredData}
        rowKey="id"
        pagination={{ pageSize: 10 }}
      />

      <MeterForm
        visible={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        onSubmit={handleFormSubmit}
        initialValues={selectedMeter}
        subscribers={subscribers}
      />
    </div>
  );
};

export default MeterList;
